import { metric, log as graylog } from '@s4l/monitoring'
import type { FetcherRequestEventListener } from '@s4l/stayforlong'
import { useCallback, useEffect, useState } from 'react'
import { ulid } from 'ulid'
import debug from 'debug'

const log = debug('s4l:uikit:SearchComparatorLogger')

export interface LegacyProperty {
  id: number | string
}

export interface SearchComparisonData {
  countsMatch: boolean
  propertyIdsMatch: boolean
  samePropertiesDifferentOrder: boolean
  fullMatch: boolean
  legacyCount: number
  newUseCaseCount: number
  legacyTotalResults: number
  newUseCaseTotalResults: number
  countDifference: number
  missingInNewCount: number
  extraInNewCount: number
  legacyFirstIds: string
  newUseCaseFirstIds: string
  timestamp: string
}

// HTTP request body (actual payload sent to API)
type RequestPayload = Record<string, unknown>

// Normalize value for comparison - treats empty arrays and null/undefined as equivalent
function normalizeValue(value: unknown): unknown {
  // Treat empty arrays as null (both mean "no filter applied")
  if (Array.isArray(value) && value.length === 0) {
    return null
  }
  // Treat undefined as null
  if (value === undefined) {
    return null
  }
  return value
}

// Find differences between two payloads (after normalization)
function findPayloadDifferences(
  legacy: RequestPayload,
  newUseCase: RequestPayload
): Record<string, { legacy: unknown; newUseCase: unknown }> {
  const diff: Record<string, { legacy: unknown; newUseCase: unknown }> = {}
  const allKeys = new Set([...Object.keys(legacy), ...Object.keys(newUseCase)])

  allKeys.forEach(key => {
    const legacyValue = legacy[key]
    const newUseCaseValue = newUseCase[key]
    const normalizedLegacy = normalizeValue(legacyValue)
    const normalizedNewUseCase = normalizeValue(newUseCaseValue)

    if (JSON.stringify(normalizedLegacy) !== JSON.stringify(normalizedNewUseCase)) {
      diff[key] = {
        legacy: legacyValue ?? null,
        newUseCase: newUseCaseValue ?? null,
      }
    }
  })

  return diff
}

export interface SearchComparatorLoggerProps {
  legacyProperties: LegacyProperty[]
  legacyTotalResults: number
  isLegacyFetching: boolean
  currentPage: number
  /**
   * Optional: The exact request payload sent by the legacy Redux search.
   * If provided, this will be logged to Graylog for comparison with the new use case request.
   * This should be the result of buildSearchPropertiesPayload() from the legacy code.
   */
  legacyRequestPayload?: Record<string, unknown>
}

interface ComparisonResult {
  countsMatch: boolean
  propertyIdsMatch: boolean
  orderMatch: boolean
  legacy: {
    count: number
    propertyIds: string[]
  }
  newUseCase: {
    count: number
    propertyIds: string[]
  }
  differences: {
    missingInNew: string[]
    extraInNew: string[]
    samePropertiesDifferentOrder: boolean
  }
}

interface NewSearchResultJSON {
  properties: Array<{ id: string; [key: string]: unknown }>
  metadata: {
    count: number
    price: { min: number; max: number }
    [key: string]: unknown
  }
}

function compareResults(
  legacyProperties: LegacyProperty[],
  newSearchResultJSON: NewSearchResultJSON
): ComparisonResult {
  const legacyPropertyIds = legacyProperties.map(p => String(p.id))
  const newPropertiesJSON = newSearchResultJSON.properties
  const newPropertyIds = newPropertiesJSON.map(p => String(p.id))

  const countsMatch = legacyProperties.length === newPropertiesJSON.length
  const propertyIdsMatch = JSON.stringify(legacyPropertyIds) === JSON.stringify(newPropertyIds)

  const sortedLegacy = [...legacyPropertyIds].sort((a, b) => a.localeCompare(b))
  const sortedNew = [...newPropertyIds].sort((a, b) => a.localeCompare(b))
  const samePropertiesDifferentOrder =
    !propertyIdsMatch && JSON.stringify(sortedLegacy) === JSON.stringify(sortedNew)

  const missingInNew = legacyPropertyIds.filter(id => !newPropertyIds.includes(id))
  const extraInNew = newPropertyIds.filter(id => !legacyPropertyIds.includes(id))

  return {
    countsMatch,
    propertyIdsMatch,
    orderMatch: propertyIdsMatch,
    legacy: {
      count: legacyProperties.length,
      propertyIds: legacyPropertyIds,
    },
    newUseCase: {
      count: newPropertiesJSON.length,
      propertyIds: newPropertyIds,
    },
    differences: {
      missingInNew,
      extraInNew,
      samePropertiesDifferentOrder,
    },
  }
}

function buildComparisonData(
  comparison: ComparisonResult,
  legacyTotalResults: number,
  newSearchResultJSON: NewSearchResultJSON
): SearchComparisonData {
  return {
    countsMatch: comparison.countsMatch,
    propertyIdsMatch: comparison.propertyIdsMatch,
    samePropertiesDifferentOrder: comparison.differences.samePropertiesDifferentOrder,
    fullMatch: comparison.countsMatch && comparison.propertyIdsMatch,
    legacyCount: comparison.legacy.count,
    newUseCaseCount: comparison.newUseCase.count,
    legacyTotalResults,
    newUseCaseTotalResults: newSearchResultJSON.metadata.count,
    countDifference: Math.abs(comparison.legacy.count - comparison.newUseCase.count),
    missingInNewCount: comparison.differences.missingInNew.length,
    extraInNewCount: comparison.differences.extraInNew.length,
    legacyFirstIds: comparison.legacy.propertyIds.slice(0, 5).join(','),
    newUseCaseFirstIds: comparison.newUseCase.propertyIds.slice(0, 5).join(','),
    timestamp: new Date().toISOString(),
  }
}

function logComparisonResults(
  comparisonData: SearchComparisonData,
  comparison: ComparisonResult
): void {
  const fullMatchIcon = comparisonData.fullMatch ? '✅' : '❌'
  const countsMatchIcon = comparisonData.countsMatch ? '✅' : '❌'
  const idsMatchIcon = comparisonData.propertyIdsMatch ? '✅' : '❌'
  const orderIcon = comparisonData.samePropertiesDifferentOrder ? '🔀' : '➖'

  log(`
╔══════════════════════════════════════════════════════════════╗
║           🔍 SEARCH COMPARATOR RESULTS                       ║
╠══════════════════════════════════════════════════════════════╣
║  ${fullMatchIcon} Full Match: ${comparisonData.fullMatch ? 'YES' : 'NO'}
║  ${countsMatchIcon} Counts Match: ${comparisonData.countsMatch ? 'YES' : 'NO'}
║  ${idsMatchIcon} Property IDs Match: ${comparisonData.propertyIdsMatch ? 'YES' : 'NO'}
║  ${orderIcon} Same Properties Different Order: ${comparisonData.samePropertiesDifferentOrder ? 'YES' : 'NO'}
╠══════════════════════════════════════════════════════════════╣
║  📊 COUNTS                                                   ║
║  ├─ Legacy Count: ${comparisonData.legacyCount}
║  ├─ New Use Case Count: ${comparisonData.newUseCaseCount}
║  ├─ Legacy Total Results: ${comparisonData.legacyTotalResults}
║  ├─ New Use Case Total Results: ${comparisonData.newUseCaseTotalResults}
║  └─ Difference: ${comparisonData.countDifference === 0 ? '✅ 0' : `⚠️ ${comparisonData.countDifference}`}
╠══════════════════════════════════════════════════════════════╣
║  🏨 PROPERTY IDS (first 5)                                   ║
║  ├─ Legacy: [${comparisonData.legacyFirstIds || 'none'}]
║  └─ New:    [${comparisonData.newUseCaseFirstIds || 'none'}]
╠══════════════════════════════════════════════════════════════╣
║  📋 DIFFERENCES                                              ║
║  ├─ Missing in New: ${comparisonData.missingInNewCount === 0 ? '✅ 0' : `❌ ${comparisonData.missingInNewCount}`}
║  └─ Extra in New: ${comparisonData.extraInNewCount === 0 ? '✅ 0' : `⚠️ ${comparisonData.extraInNewCount}`}
╚══════════════════════════════════════════════════════════════╝
      `)

  if (comparison.differences.missingInNew.length > 0) {
    log(`❌ Missing in New: [${comparison.differences.missingInNew.join(', ')}]`)
  }
  if (comparison.differences.extraInNew.length > 0) {
    log(`⚠️ Extra in New: [${comparison.differences.extraInNew.join(', ')}]`)
  }
}

// Extract HTTP request body from fetch Request object
const extractRequestBody = async (request: Request): Promise<RequestPayload | null> => {
  if (request.method !== 'POST' && request.method !== 'PUT') {
    return null
  }

  try {
    const clonedRequest = request.clone()
    const text = await clonedRequest.text()
    if (text) {
      return JSON.parse(text) as RequestPayload
    }
  } catch (error) {
    log('Error parsing request body:', error)
  }

  return null
}

/**
 * SearchComparatorLogger
 *
 * Temporary component for migration validation. Subscribes to new DDD use case search events
 * and compares results with legacy Redux search to ensure parity. Logs differences to console
 * and sends metrics to DataDog/Graylog for monitoring.
 */
export const SearchComparatorLogger = ({
  legacyProperties,
  legacyTotalResults,
  isLegacyFetching,
  currentPage,
  legacyRequestPayload,
}: SearchComparatorLoggerProps) => {
  // Store the latest new use case result
  const [newUseCaseResult, setNewUseCaseResult] = useState<NewSearchResultJSON | null>(null)
  const [newUseCaseRequest, setNewUseCaseRequest] = useState<RequestPayload | null>(null)

  const processComparison = useCallback(
    async (resultJSON: NewSearchResultJSON, newUseCaseRequestPayload: RequestPayload) => {
      // For pagination, legacy accumulates all properties but new use case returns only current page
      // Slice legacy to compare only the current page's properties
      const PROPERTIES_PER_PAGE = 18
      const startIndex = (currentPage - 1) * PROPERTIES_PER_PAGE
      const currentPageLegacyProperties = legacyProperties.slice(
        startIndex,
        startIndex + PROPERTIES_PER_PAGE
      )

      const comparison = compareResults(currentPageLegacyProperties, resultJSON)
      const comparisonData = buildComparisonData(comparison, legacyTotalResults, resultJSON)

      const responsesMatch = comparisonData.fullMatch

      // Always log comparison results to console
      logComparisonResults(comparisonData, comparison)

      // Send metric to DataDog
      metric.send({
        id: ulid(),
        name: 'SearchComparatorLogger',
        metadata: {
          match: responsesMatch,
        },
      })

      // If responses match, we're done - no need to send to Graylog
      if (responsesMatch) {
        return
      }

      // Responses don't match - send request diff to Graylog
      if (!legacyRequestPayload) {
        log('⚠️ Legacy request payload not available')
        return
      }

      const payloadDiff = findPayloadDifferences(legacyRequestPayload, newUseCaseRequestPayload)

      graylog.info({
        message: 'SearchComparatorLogger: Response mismatch detected',
        metadata: {
          payloadDifferences: payloadDiff,
          comparison: comparisonData,
        },
      })
    },
    [legacyProperties, legacyTotalResults, currentPage, legacyRequestPayload]
  )

  // Listen to fetcher events to capture search requests and responses
  const onFetcherRequest = useCallback<FetcherRequestEventListener>(async event => {
    const { request, response } = event.detail

    // Only process search API requests
    if (!request.url.includes('/v3/search')) {
      return
    }

    try {
      // Clone response to avoid consuming the stream
      const clonedResponse = response.clone()
      const responseData = await clonedResponse.json()

      // Check if this is a valid search response
      if (responseData?.properties && Array.isArray(responseData.properties)) {
        setNewUseCaseResult(responseData as NewSearchResultJSON)

        // Extract HTTP request body for comparison
        const requestBody = await extractRequestBody(request)
        if (requestBody) {
          setNewUseCaseRequest(requestBody)
        }
      }
    } catch (error) {
      log('Error processing fetcher event:', error)
    }
  }, [])

  // Register fetcher event listener
  useEffect(() => {
    window.addEventListener('stayforlong:fetcher:request', onFetcherRequest as EventListener)

    return () => {
      window.removeEventListener('stayforlong:fetcher:request', onFetcherRequest as EventListener)
    }
  }, [onFetcherRequest])

  // Process comparison when both searches complete
  useEffect(() => {
    if (!newUseCaseResult || !newUseCaseRequest || isLegacyFetching) {
      return
    }

    // Wait for legacy payload to be available
    if (!legacyRequestPayload) {
      return
    }

    processComparison(newUseCaseResult, newUseCaseRequest)
    setNewUseCaseResult(null)
    setNewUseCaseRequest(null)
  }, [
    newUseCaseResult,
    newUseCaseRequest,
    isLegacyFetching,
    legacyRequestPayload,
    processComparison,
  ])

  return null
}

SearchComparatorLogger.displayName = 'SearchComparatorLogger'
export default SearchComparatorLogger
